import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomerService } from "services/CustomerService";
import { GetCustomerDashboardDto } from "services/Dtos/CustomerDtos";
import EntitySwitcher, { SwitcherOption } from "components/common/EntitySwitcher";
import { searchCompanies } from "helpers/entitySearch";
import { CompanyContext, CompanyContextValue, companyPath } from "./CompanyContext";

const SECTION_LABELS: Record<string, string> = {
  overview: "Overview",
  "api-keys": "API Keys",
  "email-messages": "Email Messages",
  "email-addresses": "Email Addresses",
  "sms-messages": "SMS Messages",
  "phone-numbers": "Phone Numbers",
};

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  `btn text-start w-100 py-2 px-3 rounded-2 border-0 border-start border-3 ${
    isActive ? "border-primary text-primary fw-semibold bg-primary bg-opacity-10" : "border-light text-secondary"
  }`;

const NavGroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="section-nav-label px-3 mt-3 mb-1">{children}</div>
);

const CompanyLayout: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const companyId = Number(customerId);
  const section = location.pathname.split("/").filter(Boolean).pop() ?? "overview";

  const [company, setCompany] = useState<GetCustomerDashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCompany(null);
    setError(null);
    if (!Number.isFinite(companyId)) {
      setError("Invalid company ID");
      return;
    }
    let cancelled = false;
    CustomerService.customer
      .getCustomer(companyId)
      .then((res) => {
        if (cancelled) return;
        if (res.data) setCompany(res.data);
        else setError("Company not found");
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load company details");
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const contextValue: CompanyContextValue = useMemo(() => ({ companyId, company }), [companyId, company]);

  // Switching company keeps the section the user is looking at.
  const handleCompanySwitch = (option: SwitcherOption) => {
    navigate(companyPath(option.id, SECTION_LABELS[section] ? section : "overview"));
  };

  const companyName = company?.name ?? `Company #${customerId}`;
  const base = `/companies/${companyId}`;

  return (
    <div className="container-fluid p-4">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb align-items-center mb-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard" className="text-decoration-none">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/companies" className="text-decoration-none">Companies</Link>
          </li>
          <li className="breadcrumb-item d-flex align-items-center">
            <Link to={companyPath(companyId)} className="text-decoration-none fw-semibold">{companyName}</Link>
            <EntitySwitcher
              ariaLabel="Switch company"
              placeholder="Search companies..."
              search={searchCompanies}
              onSelect={handleCompanySwitch}
              currentId={companyId}
              trigger={<i className="bi bi-chevron-down small"></i>}
              triggerClassName="btn btn-sm btn-link py-0 px-1 text-secondary"
            />
          </li>
          {SECTION_LABELS[section] && (
            <li className="breadcrumb-item active" aria-current="page">{SECTION_LABELS[section]}</li>
          )}
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-3 col-xl-2 mb-4">
          <nav className="d-flex flex-column sticky-top pt-1 section-nav" aria-label="Company sections">
            <NavLink to={`${base}/overview`} className={navItemClass}>
              <i className="bi bi-graph-up me-2"></i>Overview
            </NavLink>
            <NavLink to={`${base}/api-keys`} className={navItemClass}>
              <i className="bi bi-key me-2"></i>API Keys
            </NavLink>

            <NavGroupLabel>Email</NavGroupLabel>
            <NavLink to={`${base}/email-messages`} className={navItemClass}>
              <i className="bi bi-envelope-open me-2"></i>Email Messages
            </NavLink>
            <NavLink to={`${base}/email-addresses`} className={navItemClass}>
              <i className="bi bi-envelope me-2"></i>Email Addresses
            </NavLink>

            <NavGroupLabel>SMS</NavGroupLabel>
            <NavLink to={`${base}/sms-messages`} className={navItemClass}>
              <i className="bi bi-chat-dots me-2"></i>SMS Messages
            </NavLink>
            <NavLink to={`${base}/phone-numbers`} className={navItemClass}>
              <i className="bi bi-telephone me-2"></i>Phone Numbers
            </NavLink>
          </nav>
        </div>

        <div className="col-md-9 col-xl-10">
          {error ? (
            <div className="alert alert-danger" role="alert">
              {error}{" "}
              <Link to="/companies" className="alert-link">Back to companies</Link>
            </div>
          ) : (
            <CompanyContext.Provider value={contextValue}>
              {/* Remount the page when switching company so no state leaks between companies */}
              <Outlet key={companyId} />
            </CompanyContext.Provider>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyLayout;
